import { CheckboxType } from 'src/components/atoms/Checkbox/types';

export type DDownMultiSelectType = {
  items: CheckboxType[];
  itemsSelected: CheckboxType[];
  q: string;
  checkAll?: boolean;
};

export const initialState: DDownMultiSelectType = {
  items: [],
  itemsSelected: [],
  q: '',
  checkAll: false
};

export type ActionType =
  | { type: 'load'; payload: Omit<DDownMultiSelectType, 'itemsSelected'> }
  | { type: 'selected'; payload: { item: CheckboxType } }
  | { type: 'selectAll'; payload: { check: boolean } }
  | { type: 'remove'; payload: { value: string } }
  | { type: 'removeAll' }
  | { type: 'search'; payload: { q: string } };

const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const search = (q: string, where: string) => {
  if (!q) return true;
  if (!where) return false;
  const re = new RegExp(escapeRegExp(q), 'ig');
  return where.search(re) !== -1;
};

export const DDownMultiSelectReducer = (state: DDownMultiSelectType, action: ActionType) => {
  switch (action.type) {
    case 'load': {
      const { items } = action.payload;

      const updatedItems = items.map((item) => {
        const isVisible = state.q ? search(state.q, item.label) : true;
        const isChecked = state.itemsSelected.some((selected) => selected.value === item.value);
        return {
          ...item,
          isVisible,
          isChecked
        };
      });

      const newItemValues = new Set(items.map((item) => item.value));
      const preservedSelections = state.itemsSelected.filter((selected) =>
        newItemValues.has(selected.value)
      );

      const allSelected =
        !state.q && updatedItems.length > 0 && preservedSelections.length === updatedItems.length;

      return {
        ...state,
        items: updatedItems,
        itemsSelected: preservedSelections,
        checkAll: allSelected,
        q: state.q || ''
      };
    }

    case 'selected': {
      const itemValue = action.payload.item.value;
      const isCurrentlySelected = state.itemsSelected.some((item) => item.value === itemValue);

      let newItemsSelected: CheckboxType[];
      if (action.payload.item.isChecked && !isCurrentlySelected) {
        const itemFromState = state.items.find((item) => item.value === itemValue);
        if (!itemFromState) {
          return state;
        }
        newItemsSelected = [
          ...state.itemsSelected,
          { ...itemFromState, isChecked: true } as CheckboxType
        ];
      } else {
        newItemsSelected = state.itemsSelected.filter((item) => item.value !== itemValue);
      }

      const updatedItems = state.items.map((item) =>
        item.value === itemValue ? { ...item, isChecked: action.payload.item.isChecked } : item
      );

      const allSelected =
        !state.q && updatedItems.length > 0 && newItemsSelected.length === updatedItems.length;

      return {
        ...state,
        items: updatedItems,
        itemsSelected: newItemsSelected,
        checkAll: allSelected
      };
    }

    case 'remove': {
      const newItemsSelected = state.itemsSelected.filter(
        (item) => item.value !== action.payload.value
      );

      const updatedItems = state.items.map((item) =>
        item.value === action.payload.value ? { ...item, isChecked: false } : item
      );

      return {
        ...state,
        items: updatedItems,
        checkAll: false,
        itemsSelected: newItemsSelected
      };
    }

    case 'removeAll': {
      const updatedItems = state.items.map((item) => ({ ...item, isChecked: false }));

      return {
        ...state,
        items: updatedItems,
        checkAll: false,
        itemsSelected: []
      };
    }

    case 'selectAll': {
      const isChecked = action.payload.check;

      if (isChecked) {
        const itemsToSelect = state.q ? state.items.filter((item) => item.isVisible) : state.items;
        const newItemsSelected = itemsToSelect.map((item) => ({ ...item, isChecked: true }));
        const selectedValues = new Set(newItemsSelected.map((item) => item.value));
        const updatedItems = state.items.map((item) =>
          selectedValues.has(item.value) ? { ...item, isChecked: true } : item
        );

        return {
          ...state,
          items: updatedItems,
          checkAll: !state.q && newItemsSelected.length === state.items.length,
          itemsSelected: newItemsSelected
        };
      } else {
        const updatedItems = state.items.map((item) => ({ ...item, isChecked: false }));

        return {
          ...state,
          items: updatedItems,
          checkAll: false,
          itemsSelected: []
        };
      }
    }

    case 'search': {
      const newQuery = action.payload.q;

      const updatedItems = state.items.map((item) => ({
        ...item,
        isVisible: search(newQuery, item.label)
      }));

      const allSelected =
        !newQuery && updatedItems.length > 0 && state.itemsSelected.length === updatedItems.length;

      return {
        ...state,
        q: newQuery,
        items: updatedItems,
        checkAll: allSelected
      };
    }

    default:
      return state;
  }
};
